import { useLayoutEffect, useRef, useState } from 'react'
import { DndContext, closestCenter, type DragEndEvent } from '@dnd-kit/core'
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { buzzOnPickUp, useDragSensors } from '@/hooks/useDragSensors'
import { useDraft } from '@/hooks/useDraft'
import type { ChecklistStep } from '@/types'
import { CheckIcon, DragIcon, PlusIcon, TrashIcon } from './icons'
import './Checklist.css'

interface ChecklistProps {
  steps: ChecklistStep[]
  onChange: (steps: ChecklistStep[]) => void
}

/**
 * The checklist in the detail panel: steps are added, ticked, renamed,
 * reordered and deleted here. Each step has a drag handle and a delete button.
 */
export function Checklist({ steps, onChange }: ChecklistProps) {
  const [draft, setDraft] = useState('')
  const addInput = useRef<HTMLInputElement>(null)
  // the handle alone starts a drag, so a shorter press is enough here
  const sensors = useDragSensors(150)

  const add = () => {
    const text = draft.trim()
    if (!text) return
    setDraft('')
    onChange([...steps, { id: newId(), text, done: false }])
  }

  const onDragEnd = (e: DragEndEvent) => {
    const { active, over } = e
    if (!over || active.id === over.id) return
    const from = steps.findIndex((s) => s.id === active.id)
    const to = steps.findIndex((s) => s.id === over.id)
    if (from < 0 || to < 0) return
    onChange(arrayMove(steps, from, to))
  }

  return (
    <div className="checklist">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={buzzOnPickUp} onDragEnd={onDragEnd}>
        <SortableContext items={steps.map((s) => s.id)} strategy={verticalListSortingStrategy}>
          {steps.map((s) => (
            <StepRow
              key={s.id}
              step={s}
              onToggle={() => onChange(steps.map((x) => (x.id === s.id ? { ...x, done: !x.done } : x)))}
              onRename={(text) => onChange(steps.map((x) => (x.id === s.id ? { ...x, text } : x)))}
              onDelete={() => onChange(steps.filter((x) => x.id !== s.id))}
            />
          ))}
        </SortableContext>
      </DndContext>
      <div className="checklist__add">
        <PlusIcon size={16} weight={2.2} className="checklist__add-icon" />
        <label className="sr-only" htmlFor="step-add">
          Add checklist step
        </label>
        <input
          id="step-add"
          type="text"
          className="checklist__add-input"
          placeholder="Add checklist step"
          value={draft}
          maxLength={200}
          ref={addInput}
          enterKeyHint="enter"
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              add()
            }
          }}
          onBlur={add}
        />
        {draft.trim() && (
          <button
            type="button"
            className="checklist__add-btn"
            aria-label="Add step"
            // keeps focus in the field, so the next step can be typed straight away
            onPointerDown={(e) => e.preventDefault()}
            onClick={() => {
              add()
              addInput.current?.focus()
            }}
          >
            <CheckIcon size={14} weight={3.2} />
          </button>
        )}
      </div>
    </div>
  )
}

function StepRow({ step, onToggle, onRename, onDelete }: { step: ChecklistStep; onToggle: () => void; onRename: (t: string) => void; onDelete: () => void }) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id: step.id })
  const text = useDraft(step.text, (v) => {
    const t = v.trim()
    if (t && t !== step.text) onRename(t)
  })
  const style = { transform: CSS.Transform.toString(transform), transition }
  const box = useRef<HTMLTextAreaElement>(null)
  // grows with its text, for browsers without field-sizing
  useLayoutEffect(() => {
    const el = box.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
  }, [text.value])
  return (
    <div ref={setNodeRef} style={style} className={`step ${step.done ? 'step--done' : ''} ${isDragging ? 'step--dragging' : ''}`}>
      <button type="button" className="step__tick" aria-pressed={step.done} aria-label={`${step.done ? 'Untick' : 'Tick'} ${step.text}`} onClick={onToggle}>
        <span className="step__box">{step.done && <CheckIcon size={12} weight={3.6} />}</span>
      </button>
      <textarea
        ref={box}
        rows={1}
        className="step__text"
        aria-label="Step"
        value={text.value}
        maxLength={200}
        enterKeyHint="done"
        // a step is one line of text that wraps; Enter finishes it rather than starting a new line
        onChange={(e) => text.onChange(e.target.value.replace(/\n/g, ' '))}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            e.currentTarget.blur()
          }
        }}
        onFocus={text.onFocus}
        onBlur={text.onBlur}
      />
      <button type="button" className="step__delete" aria-label={`Delete step ${step.text}`} onClick={onDelete}>
        <TrashIcon size={16} />
      </button>
      <button type="button" ref={setActivatorNodeRef} className="step__handle" data-sheet-swipe="off" aria-label={`Reorder ${step.text}`} {...attributes} {...listeners}>
        <DragIcon size={16} />
      </button>
    </div>
  )
}

function newId(): string {
  return Math.random().toString(36).slice(2, 10)
}
